<script setup lang="ts">

import { ref, computed, nextTick } from "vue"

/*
Generic double-click-to-edit field. Supports text, number, date and select.
For select, pass `options` as [{ label, value }]; `value` may be a primitive or an
object (matched by `id`). Pass `display` to override the read-only text (e.g. for
values that don't map cleanly onto an option).
*/

const props = defineProps<{
    value?: any,
    type?: "text" | "number" | "date" | "select",
    options?: { label: string, value: any }[],
    placeholder?: string,
    label?: string,        // optional suffix shown after the value (e.g. a unit)
    display?: string,      // optional read-only override text
}>()

const emit = defineEmits(["update"])

const fieldType = computed(() => props.type || "text")
const isEditing = ref(false)
const draft = ref<any>(null)
const draftIndex = ref<number>(-1)
const inputEl = ref<any>(null)

const toDate = (value: any): Date | null => {
    if (!value) return null
    if (value?.toDate) return value.toDate()
    const date = new Date(value)
    return isNaN(date.getTime()) ? null : date
}

const toISODate = (value: any): string => {
    const date = toDate(value)
    return date ? date.toISOString().slice(0, 10) : ""
}

const matchIndex = (): number => {
    if (!props.options) return -1
    return props.options.findIndex((option) => {
        if (option.value && typeof option.value === "object" && props.value && typeof props.value === "object") {
            return option.value.id === props.value.id
        }
        return option.value === props.value
    })
}

const displayText = computed(() => {
    if (props.display) return props.display
    if (fieldType.value === "select") {
        const index = matchIndex()
        return index >= 0 ? props.options![index].label : (props.placeholder || "-")
    }
    if (fieldType.value === "date") {
        const date = toDate(props.value)
        return date ? date.toLocaleDateString("tr-TR", { year: "numeric", month: "long", day: "numeric" }) : (props.placeholder || "-")
    }
    if (props.value === 0) return `0${props.label ? " " + props.label : ""}`
    return props.value ? `${props.value}${props.label ? " " + props.label : ""}` : (props.placeholder || "-")
})

const startEdit = async () => {
    if (fieldType.value === "select") {
        draftIndex.value = matchIndex()
    } else if (fieldType.value === "date") {
        draft.value = toISODate(props.value)
    } else {
        draft.value = props.value ?? ""
    }
    isEditing.value = true
    await nextTick()
    inputEl.value?.focus?.()
    inputEl.value?.select?.()
}

const commit = () => {
    if (!isEditing.value) return
    isEditing.value = false
    if (fieldType.value === "select") {
        if (draftIndex.value >= 0) emit("update", props.options![draftIndex.value].value)
    } else if (fieldType.value === "date") {
        if (draft.value) emit("update", new Date(draft.value))
    } else if (fieldType.value === "number") {
        const num = Number(draft.value)
        if (!isNaN(num) && num !== props.value) emit("update", num)
    } else {
        const text = (draft.value ?? "").toString().trim()
        if (text !== (props.value ?? "")) emit("update", text)
    }
}

const cancel = () => {
    isEditing.value = false
}

</script>

<template>
    <span
        v-if="!isEditing"
        class="editable-field"
        :class="{ 'editable-field--empty': !value && value !== 0 && !display }"
        title="Düzenlemek için çift tıklayın"
        @dblclick="startEdit"
    >
        {{ displayText }}
    </span>
    <select
        v-else-if="fieldType === 'select'"
        ref="inputEl"
        v-model="draftIndex"
        class="editable-input"
        @change="commit"
        @blur="commit"
        @keyup.esc="cancel"
    >
        <option :value="-1" disabled>Seçiniz</option>
        <option v-for="(opt, i) in options" :key="i" :value="i">{{ opt.label }}</option>
    </select>
    <input
        v-else-if="fieldType === 'date'"
        ref="inputEl"
        type="date"
        v-model="draft"
        class="editable-input"
        @blur="commit"
        @keyup.enter="commit"
        @keyup.esc="cancel"
    >
    <input
        v-else
        ref="inputEl"
        :type="fieldType"
        v-model="draft"
        class="editable-input"
        @blur="commit"
        @keyup.enter="commit"
        @keyup.esc="cancel"
    >
</template>

<style scoped>
.editable-field {
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.15s;
}
.editable-field:hover {
    background-color: #f3f4f6;
    text-decoration: underline;
    text-decoration-style: dotted;
    text-underline-offset: 4px;
}
.editable-field--empty {
    color: #9ca3af;
    font-style: italic;
}
.editable-input {
    width: 100%;
    max-width: 100%;
    padding: 0.3rem 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: inherit;
    font-family: inherit;
}
</style>
